import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useNavigate, useSearchParams } from "react-router"
import { useProductCustomers } from "../../../api/productsApi"
import type { CustomerRow } from "../../../api/types/masterData"
import { CollapsibleSection } from "../../shared/CollapsibleSection"
import {
  EntitySubTable,
  type SubTableColumn,
} from "../../shared/EntitySubTable"

export function ProductCustomersSection() {
  const [searchParams] = useSearchParams()
  const id = searchParams.get("id")
  const navigate = useNavigate()
  const { translate } = useTranslation()

  const { data, hasNextPage, fetchNextPage, isLoading } = useProductCustomers(
    id,
    { limit: 20 },
  )

  const customers = data?.pages.flatMap((p) => p.items) ?? []
  const total = data?.pages[0]?.total

  if (!isLoading && customers.length === 0) return null

  const columns: SubTableColumn<CustomerRow>[] = [
    {
      key: "customerNumber",
      header: translate("MC_MD_FIELD_CUSTOMER_NUMBER"),
      cell: (c) => <span className="font-mono">{c.customerNumber ?? "—"}</span>,
      className: "w-32",
    },
    {
      key: "name",
      header: translate("MC_MD_FIELD_NAME"),
      cell: (c) => <span className="block max-w-xs truncate">{c.name}</span>,
    },
    {
      key: "email",
      header: translate("MC_MD_FIELD_EMAIL"),
      cell: (c) => c.email ?? "—",
      hideBelow: "480px",
    },
  ]

  return (
    <CollapsibleSection
      label={translate("MC_MD_TAB_CUSTOMERS")}
      badge={
        total !== undefined && (
          <span className="text-xs text-grey5">{total}</span>
        )
      }
      className="px-4 pb-4"
      hideContentBorder
    >
      <EntitySubTable
        title={translate("MC_MD_TAB_CUSTOMERS")}
        items={customers}
        columns={columns}
        keyOf={(c) => c.id}
        hasMore={!!hasNextPage}
        onFetchMore={fetchNextPage}
        isLoading={isLoading}
        total={total}
        onRowClick={(c) => navigate(`../customers?id=${c.id}&source=nav`)}
        hideHeader
        bordered={false}
        className="px-0 pb-0"
        footerClassName="px-4"
      />
    </CollapsibleSection>
  )
}
